import Vue from 'vue';
import VueRouter from 'vue-router';

Vue.use(VueRouter);

/**
 * Wraps a lazy import to handle ChunkLoadError after deployment.
 * When new code is deployed, old chunk files (with old hashes) are removed.
 * If a user's browser has cached index.html pointing to old chunks,
 * the import will fail. This wrapper catches that error and forces
 * a single page reload to fetch the new index.html.
 */
function lazyLoad(importFn) {
  return () => importFn().catch((error) => {
    // Check if this is a chunk load error (missing file after deploy)
    const isChunkError = (
      error.name === 'ChunkLoadError'
      || error.message?.includes('Loading chunk')
      || error.message?.includes('Loading CSS chunk')
      || error.message?.includes('Failed to fetch dynamically imported module')
    );

    if (isChunkError) {
      // Use sessionStorage to prevent infinite reload loops
      const reloadKey = 'chunk_reload_timestamp';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();

      // Only auto-reload if we haven't reloaded in the last 10 seconds
      if (!lastReload || (now - Number(lastReload)) > 10000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
        return;
      }
    }

    // Re-throw if not a chunk error or if we already tried reloading
    throw error;
  });
}

const routes = [
  {
    path: '/',
    component: lazyLoad(() => import('@/layout/Layout')),
    children: [
      {
        path: 'register',
        name: 'RegisterView',
        component: lazyLoad(() => import('@/views/SignIn/index.vue')),
        meta: { title: 'title.register' },
      },
      {
        path: 'login',
        name: 'login',
        component: lazyLoad(() => import('@/views/Login/index.vue')),
        meta: { title: 'title.login' },
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: lazyLoad(() => import('@/views/ForgotPassword/index.vue')),
        meta: { title: 'title.forgotPassword' },
      },
      {
        path: 'logout',
        name: 'Logout',
        meta: { title: 'common.logout' },
      },
      // End Logout
      // openid
      {
        path: 'oauth2/bizfly-id/callbacks/openid',
        name: 'OpenIdCallback',
        component: lazyLoad(() => import('@/views/OpenIdCallback/index.vue')),
        meta: { title: 'title.openid' },
      },
      {
        path: '*',
        redirect: { path: '/login' },
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// Global error handler for navigation failures due to chunk loading
router.onError((error) => {
  const isChunkError = (
    error.name === 'ChunkLoadError'
    || error.message?.includes('Loading chunk')
    || error.message?.includes('Loading CSS chunk')
  );

  if (isChunkError) {
    const reloadKey = 'chunk_reload_timestamp';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    if (!lastReload || (now - Number(lastReload)) > 10000) {
      sessionStorage.setItem(reloadKey, String(now));
      window.location.reload();
    }
  }
});

export default router;
